/* ============================================================
   GainsSteel Fluent 2 — Animations & Behaviors
   ------------------------------------------------------------
   Drives:
     - .gsf2-reveal scroll reveal (IntersectionObserver, .is-on)
     - .gsf2-acc accordion (single-open per parent, keyboard a11y)
     - .gsf2-sticky bottom bar visibility (observe a sentinel)
     - .gsf2-faq legacy alias for accordion (same behavior)
     - .gsf2-journey-fill progress bar (data-fill)
     - .gsf2-rev-carousel scroll-snap dot sync
     - .gsf2-mini-nav active-section highlighting

   Idempotent: safe to load once per page, no globals beyond
   `window.gsf2`.
   Respect reduced-motion preference (skip animations entirely).
   ============================================================ */
(function () {
  'use strict';
  if (window.gsf2 && window.gsf2.__loaded) return;

  var reduced = window.matchMedia &&
                window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ─── Reveal: IntersectionObserver ─────────────────────── */
  function initReveals() {
    var els = document.querySelectorAll('.gsf2-reveal');
    if (!els.length) return;
    if (reduced || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('is-on'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          e.target.classList.add('is-on');
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.1 });
    els.forEach(function (el) { io.observe(el); });
  }

  /* ─── Accordion: .gsf2-acc__item with .gsf2-acc__btn ───── */
  function toggleAcc(item) {
    if (!item) return;
    var parent = item.parentElement;
    var isOpen = item.classList.contains('is-open');
    /* close siblings */
    if (parent) {
      parent.querySelectorAll('.gsf2-acc__item.is-open').forEach(function (o) {
        if (o !== item) {
          o.classList.remove('is-open');
          var btn = o.querySelector('.gsf2-acc__btn');
          if (btn) btn.setAttribute('aria-expanded', 'false');
        }
      });
    }
    item.classList.toggle('is-open', !isOpen);
    var btn = item.querySelector('.gsf2-acc__btn');
    if (btn) btn.setAttribute('aria-expanded', !isOpen ? 'true' : 'false');
  }

  function initAccordions() {
    document.querySelectorAll('.gsf2-acc__btn').forEach(function (btn) {
      if (btn.dataset.gsf2Bound === '1') return;
      btn.dataset.gsf2Bound = '1';
      btn.addEventListener('click', function () {
        toggleAcc(btn.closest('.gsf2-acc__item'));
      });
      btn.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggleAcc(btn.closest('.gsf2-acc__item'));
        }
      });
    });
  }

  /* ─── Sticky bottom bar ───────────────────────────────────
     Strategy: observe an anchor element (id from data-gsf2-sticky-anchor
     or the first .gsf2-hero / .gsf2-buybox on page). Bar shows when the
     anchor is no longer intersecting the viewport AND the footer is not
     within view (so we don't overlap the footer CTA). ─────── */
  function initSticky() {
    var bar = document.querySelector('.gsf2-sticky');
    if (!bar) return;

    var anchorSel = bar.dataset.gsf2StickyAnchor;
    var anchor = anchorSel ? document.querySelector(anchorSel)
              : (document.querySelector('.gsf2-hero')
              || document.querySelector('.gsf2-buybox')
              || document.querySelector('main'));
    if (!anchor) return;

    var footer = document.querySelector('footer') || document.querySelector('.gsf2-footer');

    function update() {
      var r = anchor.getBoundingClientRect();
      var gone = r.bottom < 0;
      var nearFooter = footer && (footer.getBoundingClientRect().top < window.innerHeight);
      bar.classList.toggle('is-on', gone && !nearFooter);
    }

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function () { update(); }, { threshold: 0 }).observe(anchor);
    }
    window.addEventListener('scroll', update, { passive: true });
    update();
  }

  /* ─── Journey fill bar ─────────────────────────────────────
     Element has class .gsf2-journey-fill and data-fill="45%".
     Animates width once when its container scrolls into view. */
  function initJourneyFill() {
    var fill = document.querySelector('.gsf2-journey-fill');
    if (!fill) return;
    var target = fill.dataset.fill || '45%';
    if (reduced || !('IntersectionObserver' in window)) {
      fill.style.width = target;
      return;
    }
    var container = fill.parentElement;
    new IntersectionObserver(function (entries, ob) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          fill.style.width = target;
          ob.unobserve(container);
        }
      });
    }, { threshold: 0.5 }).observe(container);
  }

  /* ─── Review carousel: scroll-snap dot sync ──────────────── */
  function initRevCarousels() {
    document.querySelectorAll('[data-gsf2-rev-carousel]').forEach(function (track) {
      if (track.dataset.gsf2Bound === '1') return;
      track.dataset.gsf2Bound = '1';

      var dotGroup = document.querySelector(track.dataset.gsf2RevCarousel);
      if (!dotGroup) return;
      var cards = track.querySelectorAll('.gsf2-rev-card');
      var dots = dotGroup.querySelectorAll('.gsf2-rev-dot');

      dots.forEach(function (dot, i) {
        dot.addEventListener('click', function () {
          var c = cards[i];
          if (c) track.scrollTo({ left: c.offsetLeft - 12, behavior: 'smooth' });
        });
      });

      track.addEventListener('scroll', function () {
        var mid = track.scrollLeft + track.offsetWidth / 2;
        var closest = 0;
        var minD = Infinity;
        cards.forEach(function (c, i) {
          var d = Math.abs((c.offsetLeft + c.offsetWidth / 2) - mid);
          if (d < minD) { minD = d; closest = i; }
        });
        dots.forEach(function (d, i) {
          var on = i === closest;
          d.classList.toggle('is-active', on);
          d.setAttribute('aria-selected', on ? 'true' : 'false');
        });
      }, { passive: true });
    });
  }

  /* ─── Mini-nav active section highlight ──────────────────── */
  function initMiniNav() {
    var nav = document.querySelector('.gsf2-mini-nav');
    if (!nav) return;
    var links = nav.querySelectorAll('a[href^="#"]');
    var sections = [];
    links.forEach(function (a) {
      var id = a.getAttribute('href').slice(1);
      var sec = document.getElementById(id);
      if (sec) sections.push({ link: a, sec: sec });
    });
    if (!sections.length) return;
    function update() {
      var y = window.scrollY + 120;
      var active = sections[0];
      sections.forEach(function (s) {
        if (s.sec.offsetTop <= y) active = s;
      });
      links.forEach(function (l) { l.classList.remove('is-on'); });
      if (active) active.link.classList.add('is-on');
    }
    window.addEventListener('scroll', update, { passive: true });
    update();
  }

  /* ─── Variant select (landing pricing & PDP buybox) ───────
     Click handler for [data-gsf2-var]. Updates all elements
     bearing [data-gsf2-price-target] with the price string and
     [data-gsf2-name-target] with the bundle name. Used for
     the visual-only variant picker; the real Shopify form
     submission lives in the PDP buybox section.            ── */
  function initVariantPickers() {
    var groups = document.querySelectorAll('[data-gsf2-var-group]');
    groups.forEach(function (group) {
      if (group.dataset.gsf2Bound === '1') return;
      group.dataset.gsf2Bound = '1';
      var opts = group.querySelectorAll('[data-gsf2-var]');
      opts.forEach(function (opt) {
        opt.addEventListener('click', function () { selectVariant(group, opt); });
        opt.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectVariant(group, opt); }
        });
      });
    });
  }
  function selectVariant(group, opt) {
    group.querySelectorAll('[data-gsf2-var]').forEach(function (o) {
      o.classList.remove('is-on');
      o.setAttribute('aria-pressed', 'false');
    });
    opt.classList.add('is-on');
    opt.setAttribute('aria-pressed', 'true');
    var price = opt.dataset.gsf2VarPrice;
    var name  = opt.dataset.gsf2VarName;
    if (price) {
      document.querySelectorAll('[data-gsf2-price-target]').forEach(function (el) { el.textContent = price; });
    }
    if (name) {
      document.querySelectorAll('[data-gsf2-name-target]').forEach(function (el) { el.textContent = name; });
    }
  }

  /* ─── Quantity stepper (visual; PDP wires to real form) ─── */
  function initQty() {
    document.querySelectorAll('[data-gsf2-qty]').forEach(function (qty) {
      if (qty.dataset.gsf2Bound === '1') return;
      qty.dataset.gsf2Bound = '1';
      var n = qty.querySelector('[data-gsf2-qty-n]');
      qty.querySelectorAll('[data-gsf2-qty-step]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var step = parseInt(btn.dataset.gsf2QtyStep, 10) || 0;
          var cur = parseInt(n.textContent, 10) || 1;
          n.textContent = Math.max(1, cur + step);
          /* Mirror to real input if present */
          var input = qty.parentElement && qty.parentElement.querySelector('[data-gsf2-qty-input]');
          if (input) input.value = n.textContent;
        });
      });
    });
  }

  /* ─── Public init ─────────────────────────────────────── */
  function init() {
    initReveals();
    initAccordions();
    initSticky();
    initJourneyFill();
    initRevCarousels();
    initMiniNav();
    initVariantPickers();
    initQty();
  }

  window.gsf2 = {
    __loaded: true,
    init: init,
    toggleAcc: toggleAcc,
    selectVariant: selectVariant
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Re-init on Shopify section render (theme editor) */
  document.addEventListener('shopify:section:load', init);
})();
